import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapTrifold, CaretRight, Heart } from '@phosphor-icons/react';
import { formatDuration, formatRouteDistance } from '../utils/measures';
import { plural } from '../utils/plural';
import telegramService from '../services/telegram';

const STOPS = ['точка', 'точки', 'точек'];

/**
 * Плашка редакционного маршрута — строка списка.
 *
 * Один компонент на весь проект: до этого копии жили в `FavoritesPage`,
 * `ProfilePage` и `RoutesPage` и успели разойтись по вёрстке (5.5).
 *
 * Форма отличается от карточки места намеренно: место вертикальное
 * с фото 3:4, маршрут горизонтальный с миниатюрой 72px. Раньше плашка
 * была постером с фото 160px во всю ширину — на «Маршрутах» три таких
 * занимали весь экран, хотя выбирают из них по названию и длине.
 *
 * Кнопки внутри нет: вся плашка кликабельна, а кнопка по 6.2 остаётся
 * только там, где несёт состояние. У редакционных маршрутов состояния
 * прохождения на этих экранах нет.
 *
 * Метрики идут строкой через «·» — без рамок, подложек и иконок:
 * три глифа подряд конкурируют с текстом, который поясняют.
 *
 * `onUnsave` передают там, где плашка стоит в списке сохранённого:
 * убрать из списка хотят прямо из него. На «Маршрутах» его нет —
 * там плашка показывает каталог, убирать нечего.
 */
const RouteCard = ({ route, onUnsave }) => {
  const navigate = useNavigate();
  if (!route) return null;

  const imageUrl = route.image?.url || null;
  const stopsCount = route.stops?.length || 0;

  const metrics = [
    formatDuration(route.duration),
    formatRouteDistance(route.distance),
    stopsCount > 0 ? `${stopsCount} ${plural(stopsCount, STOPS)}` : null,
  ].filter(Boolean);

  const open = () => {
    telegramService.hapticFeedback('light');
    navigate(`/routes/${route.slug}`);
  };

  return (
    <div
      onClick={open}
      className="mb-2 flex cursor-pointer items-center gap-3 overflow-hidden rounded-lg border border-theme-border bg-theme-card p-3 transition-transform duration-fast ease-out active:scale-98"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-md">
        {imageUrl ? (
          <img src={imageUrl} alt={route.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-theme-card-secondary">
            <MapTrifold size={24} weight="light" className="text-theme-text-hint" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/* Тег и оценка одной строкой над названием: раньше теги лежали
            плашками поверх фото с размытием — брендбук 5.4 его снимает.
            Тег только первый: их бывает четыре, и ряд плашек забивал
            карточку сильнее названия */}
        {(route.tags?.length > 0 || route.rating > 0) && (
          <div className="mb-0.5 flex items-center gap-2 text-kicker">
            {route.tags?.length > 0 && (
              <span className="truncate text-action-text">{route.tags[0].name}</span>
            )}
            {route.rating > 0 && (
              <span className="tabular flex-shrink-0 text-theme-text-hint">
                {String(route.rating.toFixed(1)).replace('.', ',')} ★
              </span>
            )}
          </div>
        )}

        <h3 className="truncate text-body font-semibold text-theme-text-primary">
          {route.title}
        </h3>

        {metrics.length > 0 && (
          <p className="tabular mt-1 truncate text-caption text-theme-text-secondary">
            {metrics.join(' · ')}
          </p>
        )}
      </div>

      {/* Сердце, как у карточки места: в списке сохранённого один глиф
          на все сущности — разные читались бы разными действиями.
          Маршрут при этом не удаляется, а убирается из сохранённого:
          сам он остаётся в каталоге.
          Шеврон остаётся за ним: открыть — по-прежнему главное действие */}
      {onUnsave && (
        <button
          onClick={e => { e.stopPropagation(); telegramService.hapticFeedback('light'); onUnsave(); }}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center border-0 bg-transparent transition-transform duration-fast ease-out active:scale-90"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          aria-label={`Убрать «${route.title}» из сохранённых`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-theme-favorite-bg text-theme-favorite-text">
            <Heart size={16} weight="fill" />
          </span>
        </button>
      )}

      <CaretRight size={18} className={`flex-shrink-0 text-theme-text-hint ${onUnsave ? '-ml-1' : ''}`} />
    </div>
  );
};

export default RouteCard;
